import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone, Star, MapPin } from "lucide-react";
import { CLINIC, DOCTORS, SERVICES, WHY_CHOOSE, WHO_SHOULD, MANIFESTO, TESTIMONIALS, FAQS, GENERAL_CARE } from "@/data/site";
import { Reveal, KineticHeadline, StaggerGroup, StaggerItem, EASE } from "@/components/motion/Reveal";
import Icon from "@/components/Icon";
import EditorialMarquee from "@/components/EditorialMarquee";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative pt-36 md:pt-44 pb-20 md:pb-28 overflow-hidden">
      <motion.div style={{ y: glowY }} className="pointer-events-none absolute -top-40 -left-32 w-[42rem] h-[42rem] rounded-full bg-mint blur-3xl opacity-70" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Left copy */}
        <div className="lg:col-span-7">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-brand">
              <MapPin size={13} /> Kabir Nagar, Varanasi
            </span>
          </Reveal>

          <h1 className="mt-7 font-display text-3xl md:text-5xl lg:text-[3.36vw] leading-[0.95] tracking-tight text-ink font-light">
            <KineticHeadline
              lines={["Leading Cardiologist", "in Varanasi for Heart Care,", "Diabeto-Cardiac & General", "Physician Care"]}
            />
          </h1>

          <Reveal delay={0.5}>
            <p className="mt-8 text-lg md:text-xl text-sage max-w-xl leading-relaxed">
              Dr. Pallavi Mishra offers expert consultation and personalized treatment plans for a healthier heart — with a focus on preventive, non-invasive cardiac care.
            </p>
          </Reveal>

          <Reveal delay={0.62}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={`tel:+${CLINIC.phoneRaw}`}
                data-testid="hero-book-btn"
                className="group inline-flex items-center gap-3 rounded-full bg-brand hover:bg-brand-dark text-white px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] transition-colors duration-300"
              >
                <Phone size={16} /> Book Appointment
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 rounded-full border border-line hover:border-brand text-ink px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] transition-colors duration-300"
              >
                Explore Services
              </Link>
            </div>
          </Reveal>

          {/* Trust signals */}
          <Reveal delay={0.75}>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg border-t border-line pt-8">
              {[
                { k: "MD", v: "KGMU Trained" },
                { k: "15+", v: "Years of Expertise" },
                { k: "National", v: "Chairman, Cardiabcon" },
              ].map((t) => (
                <div key={t.k}>
                  <div className="font-display text-3xl md:text-4xl text-brand leading-none">{t.k}</div>
                  <div className="mt-2 text-xs text-sage leading-snug">{t.v}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Right portrait — spotlight clipped frame + parallax */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: EASE, delay: 0.3 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] max-w-md mx-auto lg:ml-auto rounded-t-[999px] overflow-hidden border border-line bg-mint">
              <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_0%,rgba(46,139,62,0.14),transparent_60%)] z-10" />
              <motion.img
                style={{ y: imgY }}
                src={DOCTORS.pallavi.photo}
                alt="Dr. Pallavi Mishra, Consultant Cardiologist in Varanasi"
                className="absolute inset-0 w-full h-[112%] object-cover object-top -top-[6%]"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 0.9 }}
              className="absolute -bottom-4 left-2 md:-left-6 bg-surface border border-line rounded-2xl px-5 py-4 shadow-sm max-w-[15rem]"
            >
              <div className="flex items-center gap-1 text-brand mb-1">
                {[...Array(5)].map((_, i) => <Star key={i} size={13} fill="currentColor" />)}
              </div>
              <p className="text-xs text-sage leading-snug">Trusted for preventive & non-invasive cardiac care in Varanasi.</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function WhyChoose() {
  return (
    <section data-testid="why-choose" className="py-24 md:py-32 bg-surface border-y border-line">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Why Choose Dr. Pallavi Mishra</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-ink font-light max-w-3xl">
                Expertise you can trust, care that puts you first.
              </h2>
            </Reveal>
          </div>
        </div>

        <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY_CHOOSE.map((item) => (
            <StaggerItem key={item.title} className="group h-full">
              <div className="h-full rounded-3xl border border-line bg-paper p-8 transition-colors duration-300 hover:border-brand">
                <span className="grid place-items-center w-14 h-14 rounded-2xl bg-mint text-brand mb-8 transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                  <Icon name={item.icon} size={24} />
                </span>
                <h3 className="font-display text-2xl text-ink mb-3">{item.title}</h3>
                <p className="text-sm text-sage leading-relaxed">{item.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

function ServicesOverview() {
  return (
    <section data-testid="services-overview" className="py-24 md:py-32 bg-paper">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Cardiology Services</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-ink font-light">
                Comprehensive heart care, under one roof.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Reveal delay={0.1}>
              <Link to="/services" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.16em] text-brand hover:text-brand-dark transition-colors">
                View all services <ArrowUpRight size={17} />
              </Link>
            </Reveal>
          </div>
        </div>

        <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line rounded-3xl overflow-hidden border border-line">
          {SERVICES.slice(0, 6).map((s) => (
            <StaggerItem key={s.key}>
              <Link to="/services" className="group flex flex-col h-full bg-surface p-8 transition-colors duration-300 hover:bg-mint/40">
                <span className="text-brand mb-6"><Icon name={s.icon} size={26} /></span>
                <h3 className="font-display text-2xl text-ink mb-2 flex items-center gap-2">
                  {s.title}
                  <ArrowUpRight size={16} className="opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" />
                </h3>
                <p className="text-sm text-sage leading-relaxed">{s.desc}</p>
              </Link>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

function GeneralCareCallout() {
  return (
    <section data-testid="general-care-callout" className="py-24 md:py-32 bg-paper">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Beyond Cardiology</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-ink font-light">
              A trusted General Physician for everyday care.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <p className="text-lg text-sage leading-relaxed">
              Dr. Pallavi Mishra also cares for common everyday concerns — fever, seasonal illnesses, diabetes, BP, thyroid and preventive check-ups — so one trusted doctor can guide your whole health.
            </p>
          </Reveal>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <StaggerGroup className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {GENERAL_CARE.slice(0, 6).map((c) => (
            <StaggerItem key={c.id}>
              <Link to={`/general-care#${c.id}`} className="group flex flex-col h-full rounded-2xl border border-line bg-surface p-5 transition-colors duration-300 hover:border-brand hover:bg-mint/40">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-mint text-brand mb-4 transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                  <Icon name={c.icon} size={20} />
                </span>
                <span className="text-sm font-semibold text-ink leading-snug">{c.title}</span>
              </Link>
            </StaggerItem>
          ))}
        </StaggerGroup>
        <Reveal delay={0.1}>
          <Link
            to="/general-care"
            data-testid="general-care-cta"
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.16em] text-brand hover:text-brand-dark transition-colors"
          >
            Explore all general care <ArrowUpRight size={17} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function WhoShould() {
  return (
    <section data-testid="who-should" className="py-24 md:py-32 bg-brand-deep text-white grain relative overflow-hidden">
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Who Should Consult</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight font-light text-balance">
              Is it time to look after your heart?
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <StaggerGroup className="divide-y divide-white/10 border-t border-white/10">
            {WHO_SHOULD.map((item, i) => (
              <StaggerItem key={i}>
                <div className="flex items-start gap-6 py-6">
                  <span className="font-display text-2xl text-brand w-10 shrink-0">0{i + 1}</span>
                  <p className="text-lg md:text-xl text-mint/85 leading-snug">{item}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </div>
    </section>
  );
}

function PreventiveHighlight() {
  return (
    <section data-testid="preventive-highlight" className="py-24 md:py-32 bg-surface border-y border-line">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Preventive Heart Care</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-ink font-light text-balance">
                The best treatment is the one you never need.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:pt-4">
            <Reveal delay={0.1}>
              <p className="text-lg text-sage leading-relaxed">
                Early detection and prevention change everything. Through risk assessment, lifestyle modification and regular check-ups, Dr. Mishra helps you stay ahead of heart disease.
              </p>
              <Link to="/preventive-heart-care" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.16em] text-brand hover:text-brand-dark transition-colors">
                Learn about preventive care <ArrowUpRight size={17} />
              </Link>
            </Reveal>
          </div>
        </div>

        <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line rounded-3xl overflow-hidden">
          {MANIFESTO.map((m) => (
            <StaggerItem key={m.n}>
              <div className="bg-paper p-8 md:p-10 h-full">
                <span className="font-display text-6xl md:text-7xl text-mint leading-none">{m.n}</span>
                <h3 className="font-display text-2xl md:text-3xl text-ink mt-4 mb-3">{m.title}</h3>
                <p className="text-sm md:text-base text-sage leading-relaxed">{m.body}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section data-testid="testimonials" className="py-24 md:py-32 bg-paper">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Patient Stories</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-ink font-light max-w-3xl mb-14">
            Trusted by hearts across Varanasi.
          </h2>
        </Reveal>

        <Carousel opts={{ align: "start", loop: true }} className="w-full">
          <CarouselContent className="-ml-6">
            {TESTIMONIALS.map((t, i) => (
              <CarouselItem key={i} className="pl-6 md:basis-1/2" data-testid={`testimonial-${i}`}>
                <figure className="h-full rounded-3xl border border-line bg-surface p-8 md:p-10 flex flex-col">
                  <div className="flex items-center gap-1 text-brand mb-6">
                    {[...Array(5)].map((_, s) => <Star key={s} size={15} fill="currentColor" />)}
                  </div>
                  <blockquote className="font-display text-2xl md:text-3xl leading-snug text-ink flex-1">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-8 pt-6 border-t border-line">
                    <div className="font-semibold text-ink">{t.name}</div>
                    <div className="text-sm text-sage">{t.place}</div>
                  </figcaption>
                </figure>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex items-center gap-3 mt-10">
            <CarouselPrevious className="static translate-y-0 h-12 w-12 border-line text-ink hover:bg-brand hover:text-white hover:border-brand" data-testid="testimonial-prev" />
            <CarouselNext className="static translate-y-0 h-12 w-12 border-line text-ink hover:bg-brand hover:text-white hover:border-brand" data-testid="testimonial-next" />
          </div>
        </Carousel>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <EditorialMarquee />
      <WhyChoose />
      <ServicesOverview />
      <GeneralCareCallout />
      <WhoShould />
      <PreventiveHighlight />
      <Testimonials />
      <FAQSection items={FAQS.home} />
      <CTASection />
    </>
  );
}
